import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { useOutletContext } from 'react-router-dom';
import { Crown, Search, Filter, Plus, Trash2, Calendar, User, FileText, X, Mail, Building2, Eye, Send } from 'lucide-react';
import '../../styles/AdminStyles.css';

/**
 * CuratorFeeWaivers - Curator-level fee waiver management
 * 
 * Access Model: Collection-Centric
 * - Curators see only waivers for submissions linked to their organization's collections
 * - Access hierarchy: Organization → Collection → Submission → Waiver
 * - Uses organization_members table for access control (not gallery_access)
 */
const CuratorFeeWaivers = () => {
  const { user } = useAuth();
  const { selectedOrganization } = useOutletContext() || {};
  const [waivers, setWaivers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [organizationMemberships, setOrganizationMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creatingWaiver, setCreatingWaiver] = useState(false);
  const [newWaiver, setNewWaiver] = useState({
    submission_id: '',
    email: '',
    waiver_type: 'invitation',
    expires_in_days: 30,
    notes: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    invitations: 0
  });
  
  // Email preview state
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [previewWaiver, setPreviewWaiver] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (user) {
      fetchOrganizationMemberships();
    }
  }, [user]);

  useEffect(() => {
    if (organizationMemberships.length > 0) {
      console.log('CuratorFeeWaivers: selectedOrganization changed:', selectedOrganization?.organization_id);
      fetchCuratorSubmissions();
      fetchCuratorWaivers();
    }
  }, [organizationMemberships, selectedOrganization?.organization_id]);

  /**
   * Get curator's organization memberships
   * This determines which organizations (and their collections/submissions) the curator can access
   */
  const fetchOrganizationMemberships = async () => {
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          organization_id,
          role,
          organizations:organization_id (
            organization_id,
            name,
            slug
          )
        `)
        .eq('user_uuid', user.id)
        .eq('is_active', true);

      if (error) throw error;
      setOrganizationMemberships(data || []);
    } catch (error) {
      console.error('Error fetching organization memberships:', error);
    }
  };

  /**
   * Fetch submissions that belong to curator's organization's collections
   * Access chain: Organization → Collection → Submission → Waiver
   * Now filters by selected organization from sidebar
   */
  const fetchCuratorSubmissions = async () => {
    try {
      // Use selected organization if available, otherwise fall back to all memberships
      const organizationIds = selectedOrganization?.organization_id 
        ? [selectedOrganization.organization_id]
        : organizationMemberships.map(m => m.organization_id);
      
      if (organizationIds.length === 0) {
        setSubmissions([]);
        return;
      }

      // Get collections for curator's organizations
      const { data: collections, error: collectionsError } = await supabase
        .from('collections')
        .select('collection_id, title, organization_id')
        .in('organization_id', organizationIds);

      if (collectionsError) throw collectionsError;

      const collectionIds = collections?.map(c => c.collection_id) || [];

      if (collectionIds.length === 0) {
        setSubmissions([]);
        return;
      }

      // Get submissions for those collections
      const { data, error } = await supabase
        .from('submissions_details')
        .select(`
          submission_id,
          title,
          collection_id,
          collections(title, organization_id)
        `)
        .in('collection_id', collectionIds)
        .order('title', { ascending: true });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching curator submissions:', error);
    }
  };

  /**
   * Fetch waivers for curator's organization's submissions only
   * Uses RLS policy: "Organization curators can manage submission waivers"
   * Now filters by selected organization from sidebar
   */
  const fetchCuratorWaivers = async () => {
    try {
      setLoading(true);
      
      // Use selected organization if available, otherwise fall back to all memberships
      const organizationIds = selectedOrganization?.organization_id 
        ? [selectedOrganization.organization_id]
        : organizationMemberships.map(m => m.organization_id);
      
      if (organizationIds.length === 0) {
        setWaivers([]);
        setLoading(false);
        return;
      }

      // Get collections for curator's organizations
      const { data: collections, error: collectionsError } = await supabase
        .from('collections')
        .select('collection_id')
        .in('organization_id', organizationIds);

      if (collectionsError) throw collectionsError;

      const collectionIds = collections?.map(c => c.collection_id) || [];

      if (collectionIds.length === 0) {
        setWaivers([]);
        setLoading(false);
        return;
      }

      // Get submissions for those collections
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('submissions_details')
        .select('submission_id')
        .in('collection_id', collectionIds);

      if (submissionsError) throw submissionsError;

      const submissionIds = submissionsData?.map(s => s.submission_id) || [];

      if (submissionIds.length === 0) {
        setWaivers([]);
        setLoading(false);
        return;
      }

      // Fetch waivers for curator's submissions
      const { data: waiversData, error: waiversError } = await supabase
        .from('artist_submission_waivers')
        .select('*')
        .in('submission_id', submissionIds)
        .order('created_at', { ascending: false });

      if (waiversError) throw waiversError;

      // Enrich waivers with user and submission data
      const enrichedWaivers = await Promise.all(
        (waiversData || []).map(async (waiver) => {
          const enrichedWaiver = { ...waiver };

          // Fetch user data
          if (waiver.user_id) {
            const { data: userData } = await supabase
              .from('users')
              .select('uuid, email, first_name, last_name')
              .eq('uuid', waiver.user_id)
              .single();
            enrichedWaiver.users = userData;
          }

          // Fetch invited_by user data
          if (waiver.invited_by) {
            const { data: invitedByUser } = await supabase
              .from('users')
              .select('uuid, email, first_name, last_name')
              .eq('uuid', waiver.invited_by)
              .single();
            enrichedWaiver.invited_by_users = invitedByUser;
          }

          // Fetch submission data with collection
          if (waiver.submission_id) {
            const { data: submission } = await supabase
              .from('submissions_details')
              .select(`
                submission_id, 
                title, 
                collection_id, 
                submission_deadline,
                collections(title, organization_id)
              `)
              .eq('submission_id', waiver.submission_id)
              .single();
            enrichedWaiver.submissions_details = submission;
          }

          return enrichedWaiver;
        })
      );

      const processedWaivers = enrichedWaivers.map(waiver => ({
        ...waiver,
        isExpired: waiver.expires_at ? new Date(waiver.expires_at) < new Date() : false,
        daysUntilExpiry: waiver.expires_at ? 
          Math.ceil((new Date(waiver.expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : null
      }));
      
      setWaivers(processedWaivers);
      calculateStats(processedWaivers);
    } catch (error) {
      console.error('Error fetching curator waivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (waiversList) => {
    const stats = {
      total: waiversList.length,
      active: waiversList.filter(w => !w.isExpired).length,
      expired: waiversList.filter(w => w.isExpired).length,
      invitations: waiversList.filter(w => w.waiver_type === 'invitation').length
    };
    setStats(stats);
  };

  const createWaiver = async (e) => {
    e.preventDefault();
    setCreatingWaiver(true);

    try {
      // Validate required fields
      if (!newWaiver.submission_id || !newWaiver.email) {
        throw new Error('Please fill in all required fields');
      }

      // Verify curator has access to this submission
      const selectedSubmission = submissions.find(s => s.submission_id === parseInt(newWaiver.submission_id));
      if (!selectedSubmission) {
        throw new Error('You do not have access to create waivers for this submission');
      }

      // First, find the user by email
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('uuid')
        .eq('email', newWaiver.email)
        .single();

      if (userError) {
        throw new Error('User not found with that email address. They must have a VibrantCast account first.');
      }

      // Check if waiver already exists
      const { data: existingWaiver } = await supabase
        .from('artist_submission_waivers')
        .select('id')
        .eq('user_id', userData.uuid)
        .eq('submission_id', parseInt(newWaiver.submission_id))
        .single();

      if (existingWaiver) {
        throw new Error('Fee waiver already exists for this user and submission');
      }

      // Create the waiver with 'curator' type
      const waiverData = {
        user_id: userData.uuid,
        submission_id: parseInt(newWaiver.submission_id),
        waiver_type: 'curator', // Curator-created waivers get 'curator' type
        invited_by: user.id,
        notes: newWaiver.notes,
        expires_at: new Date(Date.now() + newWaiver.expires_in_days * 24 * 60 * 60 * 1000).toISOString()
      };

      const { error } = await supabase
        .from('artist_submission_waivers')
        .insert([waiverData]);

      if (error) throw error;

      // Create corresponding artist_submission_mapping record for acceptance tracking
      const mappingData = {
        user_id: userData.uuid,
        submission_id: parseInt(newWaiver.submission_id),
        purchase_id: null,
        paid: true,
        accepted: true,
        acceptance_uuid: crypto.randomUUID()
      };

      const { error: mappingError } = await supabase
        .from('artist_submission_mapping')
        .insert([mappingData]);

      if (mappingError) {
        console.error('Warning: Created waiver but failed to create mapping record:', mappingError);
      }

      // Reset form and refresh list
      setNewWaiver({
        submission_id: '',
        email: '',
        waiver_type: 'invitation',
        expires_in_days: 30,
        notes: ''
      });
      setShowCreateForm(false);
      fetchCuratorWaivers();

      alert('Fee waiver created successfully! The artist can now submit for free.');
    } catch (error) {
      console.error('Error creating waiver:', error);
      alert(error.message);
    } finally {
      setCreatingWaiver(false);
    }
  };

  const deleteWaiver = async (waiverId) => {
    if (!confirm('Are you sure you want to delete this fee waiver? The artist will need to pay to submit.')) return;

    try {
      // Get the waiver details first
      const { data: waiverData, error: waiverFetchError } = await supabase
        .from('artist_submission_waivers')
        .select('user_id, submission_id')
        .eq('id', waiverId)
        .single();

      if (waiverFetchError) throw waiverFetchError;

      // Delete the waiver
      const { error } = await supabase
        .from('artist_submission_waivers')
        .delete()
        .eq('id', waiverId);

      if (error) throw error;

      // Also delete the corresponding mapping record if it exists
      if (waiverData) {
        const { error: mappingDeleteError } = await supabase
          .from('artist_submission_mapping')
          .delete()
          .eq('user_id', waiverData.user_id)
          .eq('submission_id', waiverData.submission_id);

        if (mappingDeleteError) {
          console.error('Warning: Deleted waiver but failed to delete mapping record:', mappingDeleteError);
        }
      }
      
      fetchCuratorWaivers();
      alert('Fee waiver deleted successfully.');
    } catch (error) {
      console.error('Error deleting waiver:', error);
      alert('Failed to delete fee waiver');
    }
  };

  // Open email preview modal
  const openEmailPreview = (waiver) => {
    setPreviewWaiver(waiver);
    setShowEmailPreview(true);
  };

  // Close email preview modal
  const closeEmailPreview = () => {
    setShowEmailPreview(false);
    setPreviewWaiver(null);
  };

  // Get email preview data for display
  const getEmailPreviewData = (waiver) => {
    if (!waiver) return null;
    
    return {
      to: waiver.users?.email || 'Unknown',
      artistName: `${waiver.users?.first_name || ''} ${waiver.users?.last_name || ''}`.trim() || 'Artist',
      submissionTitle: waiver.submissions_details?.title || 'Submission',
      collectionName: waiver.submissions_details?.collections?.title || '',
      expiryDate: waiver.expires_at ? new Date(waiver.expires_at).toLocaleDateString() : 'No expiry',
      deadline: waiver.submissions_details?.submission_deadline 
        ? new Date(waiver.submissions_details.submission_deadline).toLocaleDateString() 
        : 'Contact us for details',
      invitedBy: waiver.invited_by_users 
        ? `${waiver.invited_by_users.first_name || ''} ${waiver.invited_by_users.last_name || ''}`.trim() 
        : 'Your Curator',
      submissionUrl: `https://vibrantcast.com/artist/submissions/${waiver.submission_id || ''}?submissionId=${waiver.submission_id || ''}`,
      notes: waiver.notes || ''
    };
  };

  // Actually send the email
  const sendEmailInvitation = async () => {
    if (!previewWaiver) return;
    
    setSendingEmail(true);
    
    try {
      const emailData = {
        template_name: 'fee-waiver-invitation',
        message: {
          to: [
            {
              email: previewWaiver.users?.email,
              name: `${previewWaiver.users?.first_name || ''} ${previewWaiver.users?.last_name || ''}`.trim()
            }
          ],
          global_merge_vars: [
            { name: 'ARTIST_FIRST_NAME', content: previewWaiver.users?.first_name || 'Artist' },
            { name: 'ARTIST_LAST_NAME', content: previewWaiver.users?.last_name || '' },
            { name: 'SUBMISSION_TITLE', content: previewWaiver.submissions_details?.title || 'Your Submission' },
            { name: 'WAIVER_TYPE', content: 'Curator Invitation' },
            { name: 'EXPIRY_DATE', content: previewWaiver.expires_at ? new Date(previewWaiver.expires_at).toLocaleDateString() : 'No expiry' },
            { name: 'SUBMISSION_ID', content: previewWaiver.submission_id || '' },
            { name: 'SUBMISSION_URL', content: `https://vibrantcast.com/artist/submissions/${previewWaiver.submission_id || ''}?submissionId=${previewWaiver.submission_id || ''}` },
            { name: 'INVITED_BY_NAME', content: previewWaiver.invited_by_users ? 
              `${previewWaiver.invited_by_users.first_name || ''} ${previewWaiver.invited_by_users.last_name || ''}`.trim() : 
              'Your Curator' },
            { name: 'COLLECTION_NAME', content: previewWaiver.submissions_details?.collections?.title || '' },
            { name: 'SUBMISSION_DEADLINE', content: previewWaiver.submissions_details?.submission_deadline ? 
              new Date(previewWaiver.submissions_details.submission_deadline).toLocaleDateString() : 
              'Contact us for details' },
            { name: 'NOTES', content: previewWaiver.notes || '' }
          ]
        }
      };

      const { data, error } = await supabase.functions.invoke('send-mandrill-email', {
        body: emailData
      });

      if (error) throw error;

      alert(`Email invitation sent successfully to ${previewWaiver.users?.email}`);
      closeEmailPreview();
    } catch (error) {
      console.error('Error sending email invitation:', error);
      alert('Failed to send email invitation. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const getFilteredWaivers = () => {
    let filtered = waivers;

    if (searchTerm) {
      filtered = filtered.filter(waiver => 
        waiver.users?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        waiver.users?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        waiver.users?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        waiver.submissions_details?.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(waiver => waiver.waiver_type === filterType);
    }

    if (filterStatus === 'active') {
      filtered = filtered.filter(waiver => !waiver.isExpired);
    } else if (filterStatus === 'expired') {
      filtered = filtered.filter(waiver => waiver.isExpired);
    }

    return filtered;
  };

  const getWaiverTypeColor = (type) => {
    switch (type) {
      case 'invitation': return 'blue';
      case 'curator': return 'teal';
      case 'member': return 'green';
      case 'admin_override': return 'purple';
      default: return 'gray';
    }
  };

  const getStatusBadge = (waiver) => {
    if (waiver.isExpired) {
      return <span className="status-badge expired">Expired</span>;
    }
    if (waiver.daysUntilExpiry <= 7) {
      return <span className="status-badge warning">Expires Soon</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  const getSubmissionDisplayName = (submission) => {
    if (!submission) return 'Unknown';
    let name = submission.title || 'Untitled';
    if (submission.collections?.title) {
      name += ` (${submission.collections.title})`;
    }
    return name;
  };

  // Show organization info in header
  const getOrganizationNames = () => {
    return organizationMemberships
      .map(m => m.organizations?.name)
      .filter(Boolean)
      .join(', ');
  };

  if (loading) return <div className="loading-container">Loading fee waivers...</div>;

  const filteredWaivers = getFilteredWaivers();

  return (
    <div className="fee-waivers-admin curator-fee-waivers">
      <div className="admin-header">
        <div className="admin-header-left">
          <h1 className="admin-title">
            <Crown className="admin-title-icon" />
            Fee Waivers
          </h1>
          <p className="admin-subtitle">
            <Building2 size={14} style={{ display: 'inline', marginRight: '4px' }} />
            Manage artist fee waivers for your collections
            {organizationMemberships.length > 0 && (
              <span className="org-context"> • {getOrganizationNames()}</span>
            )}
          </p>
        </div>
        <div className="admin-header-right">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="create-waiver-btn"
          >
            <Plus size={20} />
            {showCreateForm ? 'Cancel' : 'Invite Artist'}
          </button>
        </div>
      </div>

      {/* Create New Waiver Form */}
      {showCreateForm && (
        <div className="create-waiver-form-container">
          <div className="create-waiver-form-header">
            <h3>Invite Artist with Fee Waiver</h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="close-form-btn"
            >
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={createWaiver} className="create-waiver-form">
            <div className="form-row">
              <div className="form-group">
                <label>Submission (Your Collections Only) *</label>
                <select
                  value={newWaiver.submission_id}
                  onChange={(e) => setNewWaiver({...newWaiver, submission_id: e.target.value})}
                  required
                  className="form-select"
                >
                  <option value="">Select a submission...</option>
                  {submissions.map((submission) => (
                    <option key={submission.submission_id} value={submission.submission_id}>
                      {getSubmissionDisplayName(submission)}
                    </option>
                  ))}
                </select>
                {submissions.length === 0 && (
                  <p className="form-help-text">No submissions found for your collections.</p>
                )}
              </div>
              
              <div className="form-group">
                <label>Artist Email *</label>
                <input
                  type="email"
                  value={newWaiver.email}
                  onChange={(e) => setNewWaiver({...newWaiver, email: e.target.value})}
                  required
                  placeholder="artist@email.com"
                  className="form-input"
                />
                <p className="form-help-text">Artist must have a VibrantCast account.</p>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Expires In (days)</label>
                <input
                  type="number"
                  value={newWaiver.expires_in_days}
                  onChange={(e) => setNewWaiver({...newWaiver, expires_in_days: parseInt(e.target.value) || 30})}
                  min="1"
                  max="365"
                  className="form-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Personal Note (optional)</label>
              <textarea
                value={newWaiver.notes}
                onChange={(e) => setNewWaiver({...newWaiver, notes: e.target.value})}
                placeholder="Add a personal message for the artist..."
                rows={3}
                className="form-textarea"
              />
            </div>
            
            <div className="form-actions">
              <button
                type="submit"
                disabled={creatingWaiver}
                className="submit-btn"
              >
                {creatingWaiver ? 'Creating...' : 'Create Fee Waiver & Invite'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stats Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-icon total">
            <Crown size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Waivers</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon active">
            <User size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.active}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon expired">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.expired}</div>
            <div className="stat-label">Expired</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon invitations">
            <FileText size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.invitations}</div>
            <div className="stat-label">Invitations</div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by artist name, email, or submission title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-controls">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="curator">Curator Invitations</option>
            <option value="invitation">Invitations</option>
            <option value="member">Members</option>
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Waivers List */}
      <div className="waivers-list-container">
        <div className="waivers-header">
          <h3>Your Fee Waivers ({filteredWaivers.length})</h3>
        </div>
        
        {filteredWaivers.length === 0 ? (
          <div className="no-waivers">
            <Crown size={48} />
            <p>No fee waivers found</p>
            <small>Invite artists to submit for free using the button above</small>
          </div>
        ) : (
          <div className="waivers-table">
            <div className="waivers-table-header">
              <div className="waiver-col artist">Artist</div>
              <div className="waiver-col submission">Submission</div>
              <div className="waiver-col type">Type</div>
              <div className="waiver-col status">Status</div>
              <div className="waiver-col dates">Dates</div>
              <div className="waiver-col actions">Actions</div>
            </div>
            
            {filteredWaivers.map((waiver) => (
              <div key={waiver.id} className="waiver-row">
                <div className="waiver-col artist">
                  <div className="artist-info">
                    <div className="artist-name">
                      {waiver.users?.first_name} {waiver.users?.last_name}
                    </div>
                    <div className="artist-email">{waiver.users?.email}</div>
                  </div>
                </div>
                
                <div className="waiver-col submission">
                  <div className="submission-info">
                    <div className="submission-title">
                      {waiver.submissions_details?.title || 'Unknown Submission'}
                    </div>
                    <div className="submission-details">
                      {waiver.submissions_details?.collections?.title || 'Unknown Collection'}
                    </div>
                  </div>
                </div>
                
                <div className="waiver-col type">
                  <span className={`waiver-type-badge ${getWaiverTypeColor(waiver.waiver_type)}`}>
                    {waiver.waiver_type === 'curator' ? 'Curator Invite' : waiver.waiver_type.replace('_', ' ')}
                  </span>
                </div>
                
                <div className="waiver-col status">
                  {getStatusBadge(waiver)}
                </div>
                
                <div className="waiver-col dates">
                  <div className="date-info">
                    <div className="created-date">
                      Created: {new Date(waiver.created_at).toLocaleDateString()}
                    </div>
                    {waiver.expires_at && (
                      <div className="expiry-date">
                        Expires: {new Date(waiver.expires_at).toLocaleDateString()}
                        {waiver.daysUntilExpiry && waiver.daysUntilExpiry > 0 && (
                          <span className="days-remaining"> ({waiver.daysUntilExpiry} days)</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="waiver-col actions">
                  <button
                    onClick={() => openEmailPreview(waiver)}
                    className="action-btn email"
                    title="Preview & send email invitation"
                    disabled={!waiver.users?.email}
                  >
                    <Mail size={16} />
                  </button>
                  <button
                    onClick={() => deleteWaiver(waiver.id)}
                    className="action-btn delete"
                    title="Delete waiver"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Email Preview Modal */}
      {showEmailPreview && previewWaiver && (
        <div className="email-preview-overlay" onClick={closeEmailPreview}>
          <div className="email-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-preview-header">
              <h3>
                <Mail size={20} />
                Email Invitation Preview
              </h3>
              <button onClick={closeEmailPreview} className="close-preview-btn">
                <X size={20} />
              </button>
            </div>
            
            <div className="email-preview-content">
              {(() => {
                const preview = getEmailPreviewData(previewWaiver);
                return (
                  <>
                    <div className="email-preview-meta">
                      <div className="preview-field">
                        <label>To:</label>
                        <span>{preview.to}</span>
                      </div>
                      <div className="preview-field">
                        <label>Subject:</label>
                        <span>You're Invited to Submit Your Artwork - Fee Waived!</span>
                      </div>
                    </div>
                    
                    <div className="email-preview-body">
                      <div className="preview-email-card">
                        <div className="preview-email-header">
                          <img 
                            src="/images/vibrantcast-logo.png" 
                            alt="VibrantCast" 
                            className="preview-logo"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <h2>You're Invited!</h2>
                        </div>
                        
                        <div className="preview-email-content">
                          <p className="greeting">Dear <strong>{preview.artistName}</strong>,</p>
                          
                          <p>
                            You have been personally invited by <strong>{preview.invitedBy}</strong> to 
                            submit your artwork to:
                          </p>
                          
                          <div className="preview-highlight-box">
                            <h3>{preview.submissionTitle}</h3>
                            {preview.collectionName && (
                              <p className="collection-name">{preview.collectionName}</p>
                            )}
                          </div>
                          
                          <div className="preview-details">
                            <div className="detail-row">
                              <Calendar size={16} />
                              <span><strong>Deadline:</strong> {preview.deadline}</span>
                            </div>
                            <div className="detail-row">
                              <Crown size={16} />
                              <span><strong>Your Fee:</strong> <span className="free-badge">FREE</span> (waiver expires {preview.expiryDate})</span>
                            </div>
                          </div>
                          
                          {preview.notes && (
                            <div className="preview-notes">
                              <p><strong>Personal Note:</strong></p>
                              <p className="notes-content">"{preview.notes}"</p>
                            </div>
                          )}
                          
                          <div className="preview-cta">
                            <a 
                              href={preview.submissionUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="preview-cta-button"
                            >
                              View Submission & Apply
                            </a>
                          </div>
                          
                          <p className="preview-footer-text">
                            We look forward to seeing your work!
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            
            <div className="email-preview-actions">
              <button 
                onClick={closeEmailPreview} 
                className="preview-cancel-btn"
              >
                Cancel
              </button>
              <button 
                onClick={sendEmailInvitation}
                disabled={sendingEmail}
                className="preview-send-btn"
              >
                <Send size={16} />
                {sendingEmail ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuratorFeeWaivers;
